import type {ComponentPropsWithoutRef, Ref} from 'react'
import type {Icon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import styles from './SidebarSection.module.css'

interface SidebarDisclosureHeaderProps extends ComponentPropsWithoutRef<'button'> {
  title: string
  /** Trailing affordance icon, aligned to the end of the row. */
  trailingIcon: Icon
  trailingIconClassName?: string
  ref?: Ref<HTMLButtonElement>
}

/**
 * Full-row clickable section header. The entire row is a single button that can
 * anchor an overlay (e.g. an `ActionMenu`). It shares `SidebarSection`'s header
 * height so the title-to-body spacing matches static section headers.
 *
 * The button is wrapped in an `<h4>` (the ARIA disclosure/accordion pattern) so
 * the section keeps heading-navigation parity with static sections, while the
 * whole row remains a single clickable target. `<button>` is phrasing content,
 * so it is valid inside a heading.
 */
export function SidebarDisclosureHeader({
  title,
  trailingIcon: TrailingIcon,
  trailingIconClassName,
  ref,
  className,
  ...props
}: SidebarDisclosureHeaderProps) {
  // Merge any className injected by an overlay anchor (e.g. ActionMenu.Anchor)
  // so it cannot clobber the disclosure header styling.
  const buttonClassName = clsx(styles.disclosureHeader, className)

  return (
    <h4 className={styles.disclosureHeading}>
      <button type="button" className={buttonClassName} ref={ref} {...props}>
        <span className={styles.sectionTitle}>{title}</span>
        <TrailingIcon className={clsx(styles.disclosureIcon, trailingIconClassName)} />
      </button>
    </h4>
  )
}
